import React, { useEffect } from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Flex } from '@lightspeed/flame/Core';

import {
  DateTermValueType,
  GlobalState,
  usePaymentTermsInputContext,
} from '../contexts/CreditStatementTermsContext';
import {
  StyledBalanceOwedValue,
  StyledInput,
  StyledInputContainer,
  StyledInputRow,
  StyledLabelContainer,
  StyledSelect,
  StyledSelectContainer,
} from './Common.style';

const LoadingBones = () => {
  return (
    <Flex
      padding="4px 8px"
      width="100%"
      height="31px"
      style={{ borderBottom: '1px solid #ddd' }}
      alignItems="center"
    >
      <Bone height="20px" />
    </Flex>
  );
};

export function PaymentDueDateInput() {
  const { setGlobalState, globalState, getDefaultDueDate } = usePaymentTermsInputContext();

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Only allow whole, non-negative numbers
    if (!event.key.match(/^\d+$/)) {
      event.preventDefault();
    }
  };

  const handleDueDateInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      dateTermValue: value,
    }));
  };

  const handleChangeDueDateType = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      target: { value },
    } = event;

    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      dateTermValueType: value as DateTermValueType,
    }));
  };

  // Handle cases where the user leaves an empty input field.
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;

    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      dateTermValue: value || getDefaultDueDate(),
    }));
  };

  useEffect(() => {
    setGlobalState((prevState: GlobalState) => ({
      ...prevState,
      dateTermValue: getDefaultDueDate(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalState.dateTermValueType]);

  useEffect(() => {
    window.merchantos.datepicker.init();
  }, []);

  const userLocale = window?.merchantos?.locale ?? {};
  const currencyCode = userLocale.currency_code ?? 'USD';
  const localeCode = userLocale.lang ?? 'en-US';
  const numberToCurrency = new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: currencyCode,
  });

  return (
    <>
      <StyledInputRow>
        <StyledLabelContainer className="label" data-test="date-range-balance-owed-label">
          Balance owed for selected time period
        </StyledLabelContainer>
        <StyledInputContainer /> {/* Empty container to fill the space */}
        {globalState.isFetchingOwedAmount ? (
          <LoadingBones />
        ) : (
          <StyledBalanceOwedValue data-test="date-range-balance-owed-value">
            <span className="notranslate">
              {numberToCurrency.format(globalState.totalAmountOwed / 100)}
            </span>
          </StyledBalanceOwedValue>
        )}
      </StyledInputRow>
      <StyledInputRow>
        <StyledLabelContainer className="label">
          <label htmlFor="view_custom_account_statement_request_payment_before">
            Request payment before
          </label>
        </StyledLabelContainer>
        <StyledInputContainer
          // Must switch between input types this way, otherwise switching between selection options
          // will result in two calendars of different styles being present due to a parent wrapper
          // being added to the date input.
          style={{
            display:
              globalState.dateTermValueType === DateTermValueType.DAYS_FROM_TODAY
                ? 'inline-block'
                : 'none',
          }}
        >
          <StyledInput
            type="number"
            autoComplete="off"
            id="view_custom_account_statement_request_payment_before"
            name="view_custom_account_statement_request_payment_before"
            className="view_view number data_control"
            value={globalState.dateTermValue}
            onChange={handleDueDateInput}
            onKeyPress={handleKeyPress}
            onBlur={handleBlur}
          />
        </StyledInputContainer>
        <StyledInputContainer
          style={{
            display:
              globalState.dateTermValueType === DateTermValueType.DAYS_FROM_TODAY
                ? 'none'
                : 'inline-block',
          }}
        >
          <StyledInput
            type="date"
            autoComplete="off"
            id="view_custom_account_statement_request_payment_before"
            name="credit_account_request_payment_before_specific_date_input"
            value={globalState.dateTermValue}
            onBlur={handleBlur}
            onChange={handleBlur}
          />
        </StyledInputContainer>
        <StyledSelectContainer>
          <StyledSelect
            name="custom_account_statement_payment_before_select"
            id="view_custom_account_statement_request_payment_before_select_type"
            className="view_view data_control"
            defaultValue={globalState.dateTermValueType}
            onChange={handleChangeDueDateType}
          >
            <option value={DateTermValueType.SPECIFIC_DATE}>Specific date</option>
            <option value={DateTermValueType.DAYS_FROM_TODAY}>Days from today</option>
          </StyledSelect>
        </StyledSelectContainer>
      </StyledInputRow>
    </>
  );
}
